import { matchingSites } from '@hbs-us/contracts';
import { formatDate, useTranslation } from '@hbs-us/ui';
import { useState } from 'react';

import { useSession } from '../../auth/useSession.js';
import { ApiError } from '../../lib/apiFetch.js';
import { ConfirmDialog } from '../_money/ConfirmDialog.js';

import type { ClockPunchInput } from './contractsMirror.js';
import { LocationOffNotice } from './LocationOffNotice.js';
import { type ClockTarget, labelForTarget, SiteCombobox } from './SiteCombobox.js';
import { useClockInMutation, useClockOutMutation, useClockStateQuery } from './useClockMutation.js';
import { useMyWeek } from './useMyWeek.js';
import { elapsedMinutesSince, useNow } from './useNow.js';

/**
 * The crew punch screen: a time card, the punch tiles, and the week behind them.
 *
 * THE LIT TILE IS THE SHIFT STATE. Exactly one tile is ever active — punch in when the shift is
 * closed, punch out when it is open — and the other is disabled rather than removed, so the grid
 * never moves under a thumb reaching for it. The reference clock this replaces offered every button
 * in every state, which let somebody press one the server was certain to refuse.
 *
 * ⚠ MEAL TILES ARE NOT HERE YET, and it is not an oversight. The database columns exist
 * (`lunch_start_at`, `lunch_end_at`, and the four lunch position columns from migration 086) but
 * NO ENDPOINT WRITES THEM: the buttons were removed in 2026-07-28 when the flat six-hour rule made
 * a punched meal unable to change anybody's pay. Rendering two tiles that cannot answer a tap would
 * be worse than leaving the row short. They come back when the endpoint does, behind a tenant flag.
 *
 * ⚠ THE SERVER OWNS EVERY HOUR. The wall clock and the elapsed line are STATUS. The authoritative
 * figure is whatever the server books at punch-out, which applies the flat six-hour lunch rule this
 * screen cannot see.
 *
 * A user without `time.clock` sees nothing rather than a control that would answer 403.
 */

/** Shared tile geometry. Tone and state classes are appended per tile. */
const TILE =
  'flex min-h-[9.25rem] flex-col items-center justify-center gap-2.5 rounded-xl border p-5 text-center ' +
  'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ' +
  'focus-visible:ring-offset-2 focus-visible:ring-offset-background';

const TILE_LABEL = 'font-semibold text-lg leading-tight';
const TILE_META = 'text-sm leading-snug';

/**
 * The disabled treatment.
 *
 * `next` is the tile that is ABOUT to become the action and is waiting on the person, not on the
 * shift: punch in, disabled only because no site has been chosen yet. It keeps the card surface and
 * a DASHED accent border, so it reads as "nearly ready" rather than as a dead box, but its text is
 * muted. It used to be a solid orange border with orange text, which is exactly how an outlined
 * button looks, so crews tapped it and nothing happened.
 *
 * ⚠ PUNCH OUT IS NEVER `next`. It was, and the screen read backwards: off the clock, the tile
 * labelled "Punch out — you are not on the clock" wore an orange border and out-pulled the greyed
 * "Punch in" beside it, which was the action actually available. A tile that cannot apply at all is
 * plain grey.
 */
function disabledTile(next: boolean): string {
  return next
    ? 'cursor-not-allowed border-dashed border-primary/60 bg-card text-muted-foreground'
    : 'cursor-not-allowed border-transparent bg-muted text-muted-foreground';
}

function PunchInMark() {
  return (
    <svg
      className="size-10 shrink-0"
      viewBox="0 0 40 40"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 6 v16" />
      <path d="M13 16 l7 7 7-7" />
      <path d="M8 32 h24" />
    </svg>
  );
}

function PunchOutMark() {
  return (
    <svg
      className="size-10 shrink-0"
      viewBox="0 0 40 40"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 23 v-16" />
      <path d="M13 13 l7 -7 7 7" />
      <path d="M8 32 h24" />
    </svg>
  );
}

function LedgerMark() {
  return (
    <svg
      className="size-9 shrink-0"
      viewBox="0 0 40 40"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M8 11 h24" />
      <path d="M8 20 h24" />
      <path d="M8 29 h16" />
    </svg>
  );
}

export function PunchTiles() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const { session } = useSession();
  const canClock = Boolean(session?.user?.permissions?.includes('time.clock'));

  const now = useNow();
  const state = useClockStateQuery(canClock);
  // The punch list AND the booked figure, joined in one place so this screen and the "My week" card
  // beside it cannot answer "how many hours" differently. See `useMyWeek` for why the grid wins and
  // why `rows[0]` would be somebody else.
  const week = useMyWeek(canClock);
  const clockIn = useClockInMutation();
  const clockOut = useClockOutMutation();

  const [target, setTarget] = useState<ClockTarget | null>(null);
  const [confirmingTyped, setConfirmingTyped] = useState(false);
  const [confirmingDuplicate, setConfirmingDuplicate] = useState(false);
  const [ledgerOpen, setLedgerOpen] = useState(false);

  if (!canClock) return null;

  const jobs = state.data?.jobs ?? [];
  const active = state.data?.activeEntry ?? null;
  const clockStateKnown = state.isSuccess;
  const busy = clockIn.isPending || clockOut.isPending;

  const failure = [clockIn.error, clockOut.error].find(Boolean) ?? null;
  const errorMessage = failure
    ? t(`timesheets:myClock.errors.${failure instanceof ApiError ? failure.message : 'generic'}`, {
        defaultValue: t('timesheets:myClock.errors.generic'),
      })
    : null;

  /**
   * The hour, the minute, the second and the day period, each as its own string.
   *
   * ⚠ SPLIT WITH `formatToParts`, NEVER WITH A REGEX. Reading the hour and then stripping "AM" off
   * the end only works in English: Spanish formats the same instant as "7 a. m.", so a
   * `/[AP]M$/` strip leaves the period glued to the hour and the readout prints "7 a. m.:42:19".
   * A 24-hour locale returns no `dayPeriod` part at all, which is why it is rendered conditionally
   * rather than as an empty span holding open a margin.
   */
  const clockParts = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(now);
  const partValue = (type: string): string =>
    clockParts.find((part) => part.type === type)?.value ?? '';
  const parts = {
    hour: partValue('hour'),
    minute: partValue('minute'),
    second: partValue('second'),
    dayPeriod: partValue('dayPeriod'),
  };

  /** Minute resolution on purpose — see `elapsedMinutesSince`. Nothing for a punch just landed. */
  const elapsedLabel = (clockInAt: string): string | null => {
    const minutes = elapsedMinutesSince(clockInAt, now);
    if (minutes === null || minutes < 1) return null;
    const hours = Math.floor(minutes / 60);
    return hours > 0
      ? t('timesheets:myClock.elapsedHm', { hours, minutes: minutes % 60 })
      : t('timesheets:myClock.elapsedM', { minutes: minutes % 60 });
  };

  const punchFor = (chosen: ClockTarget): ClockPunchInput => {
    if (chosen.kind === 'job') return { jobId: chosen.job.id };
    if (chosen.kind === 'site') {
      return {
        siteAddress: chosen.suggestion.address,
        siteLat: chosen.suggestion.lat,
        siteLng: chosen.suggestion.lng,
      };
    }
    // Typed as-is: nobody geocoded this, so there are no site coordinates to send.
    return { siteAddress: chosen.address };
  };

  const submitPunch = (chosen: ClockTarget) => {
    clockIn.mutate(punchFor(chosen), {
      onSuccess: () => {
        setTarget(null);
        setConfirmingTyped(false);
        setConfirmingDuplicate(false);
      },
    });
  };

  /** Only a target that would MINT a site can duplicate one; picking a job creates nothing. */
  const duplicatesFor = (chosen: ClockTarget) =>
    chosen.kind === 'job' ? [] : matchingSites(labelForTarget(chosen), jobs, (job) => job.jobName);
  const duplicates = target ? duplicatesFor(target) : [];

  const startPunch = () => {
    if (!target) return;
    // "We already have this site" comes FIRST, and covers the geocoded path too: on 2026-07-31 the
    // middle of three duplicate jobs came from tapping a geocoder suggestion, so a typed-only check
    // would have stopped one and let the other through.
    if (duplicates.length > 0) {
      setConfirmingDuplicate(true);
      return;
    }
    // Only text nobody geocoded gets a dialog — that is the one that turns a typo into a site.
    if (target.kind === 'typed') {
      setConfirmingTyped(true);
      return;
    }
    submitPunch(target);
  };

  const entries = week.entries;
  // `null`, never 0, when the grid has not answered for this person: printing a confident zero for a
  // week somebody actually worked is worse than printing nothing.
  const weekHours = week.row?.totalHours ?? null;
  const punchTime = (iso: string | null): string =>
    iso ? formatDate(iso, locale, { hour: 'numeric', minute: '2-digit' }) : '—';

  return (
    <div className="flex flex-col gap-4">
      {/*
        The wall clock leads. It is never the elapsed time, so nothing large enough to read as a
        number of hours worked can be mistaken for one. The digits stay ink and only the separators
        carry the accent — the same call the shipped iOS clock makes, and the one that keeps the
        readout above the contrast floor.
      */}
      <section
        aria-labelledby="punch-clock-heading"
        className="rounded-xl border border-border bg-card p-6 text-center shadow-elev-1"
      >
        <h2 id="punch-clock-heading" className="sr-only">
          {t('timesheets:punch.heading')}
        </h2>
        <p className="flex items-baseline justify-center font-mono font-medium text-5xl text-foreground tabular-nums tracking-tight sm:text-6xl">
          <span>{parts.hour}</span>
          <span className="text-ring" aria-hidden="true">
            :
          </span>
          <span>{parts.minute}</span>
          <span className="text-ring" aria-hidden="true">
            :
          </span>
          <span>{parts.second}</span>
          {parts.dayPeriod ? (
            <span className="ml-[0.34em] text-2xl text-muted-foreground sm:text-3xl">
              {parts.dayPeriod}
            </span>
          ) : null}
        </p>
        <p className="mt-1.5 font-medium text-muted-foreground text-sm">
          {formatDate(now, locale, {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          })}
        </p>

        <p className="mt-4 flex flex-wrap items-baseline justify-center gap-x-2.5 gap-y-1.5 border-border border-t pt-4 text-sm">
          {/*
            No dot at all while the clock state is unreadable. The grey dot is the OFF-THE-CLOCK
            tone, so leaving it up states a shift status next to a line saying the status is
            unknown. The message carries its own alert role and colour; the dot adds nothing.
          */}
          {state.isError ? null : (
            <span
              className={`size-2.5 shrink-0 self-center rounded-full ${
                active ? 'bg-success' : 'bg-muted-foreground'
              }`}
              aria-hidden="true"
            />
          )}
          {state.isPending ? (
            <span className="text-muted-foreground">{t('timesheets:myClock.loading')}</span>
          ) : state.isError ? (
            <>
              <span role="alert" className="text-destructive">
                {t('timesheets:punch.clockUnavailable')}
              </span>
              {/*
                A dead end here is the whole defect: without this the screen states the clock is
                unknown, disables both tiles, and offers nothing but a full page reload.
              */}
              <button
                type="button"
                onClick={() => {
                  void state.refetch();
                  week.refetch();
                }}
                aria-label={t('timesheets:punch.retryClock')}
                className="font-medium text-foreground underline underline-offset-4"
              >
                {t('common:error.retry')}
              </button>
            </>
          ) : active ? (
            <>
              <span className="text-foreground">
                {t('timesheets:punch.onTheClockAt')}{' '}
                <b className="font-semibold">{active.jobName}</b>
              </span>
              <span className="text-muted-foreground">
                {t('timesheets:myClock.since', {
                  time: formatDate(active.clockInAt, locale, {
                    hour: 'numeric',
                    minute: '2-digit',
                  }),
                })}
                {elapsedLabel(active.clockInAt) ? ` · ${elapsedLabel(active.clockInAt)}` : ''}
              </span>
            </>
          ) : (
            <span className="text-muted-foreground">{t('timesheets:punch.offTheClock')}</span>
          )}
        </p>
      </section>

      {/*
        Off the clock the site picker sits ABOVE the tiles, because the punch cannot happen without
        it and a disabled tile with no visible reason is the bug this screen replaced. `SiteCombobox`
        is the shipped control, so typing an address arms the punch without a dropdown tap having to
        land — the failure that greyed the old button out on a phone.
      */}
      {!active && clockStateKnown ? (
        <div className="rounded-xl border border-border bg-card p-4">
          <SiteCombobox jobs={jobs} disabled={busy} onChange={setTarget} />
          {jobs.length === 0 ? (
            <p className="mt-2 text-muted-foreground text-sm">
              {t('timesheets:myClock.noJobsSearchInstead')}
            </p>
          ) : null}
        </div>
      ) : null}

      <LocationOffNotice />

      <div className="grid grid-cols-2 gap-3.5">
        <button
          type="button"
          onClick={startPunch}
          disabled={!clockStateKnown || busy || !!active || !target}
          className={`${TILE} ${
            !clockStateKnown || busy || active || !target
              ? // Waiting on a site is "nearly ready". Already being on the clock is not, and
                // neither is a clock state that failed to load — a tile wearing the accent while
                // the screen says it cannot check your clock out-pulls the retry beside it.
                disabledTile(clockStateKnown && !active && !busy)
              : 'border-primary bg-primary text-primary-foreground hover:bg-primary/90'
          }`}
        >
          <PunchInMark />
          <span className={TILE_LABEL}>{t('timesheets:punch.in')}</span>
          <span className={TILE_META}>
            {!clockStateKnown
              ? t('timesheets:punch.clockUnknown')
              : active
                ? t('timesheets:punch.alreadyOn')
                : target
                  ? labelForTarget(target)
                  : t('timesheets:punch.pickSiteFirst')}
          </span>
        </button>

        <button
          type="button"
          onClick={() => clockOut.mutate()}
          disabled={!clockStateKnown || busy || !active}
          className={`${TILE} ${
            !clockStateKnown || busy || !active
              ? disabledTile(false)
              : 'border-foreground bg-foreground text-background hover:bg-foreground/90'
          }`}
        >
          <PunchOutMark />
          <span className={TILE_LABEL}>{t('timesheets:punch.out')}</span>
          <span className={TILE_META}>
            {!clockStateKnown
              ? t('timesheets:punch.clockUnknown')
              : active
                ? (elapsedLabel(active.clockInAt) ?? '')
                : t('timesheets:punch.notOnYet')}
          </span>
        </button>
      </div>

      {errorMessage ? (
        <p role="alert" aria-live="assertive" className="text-destructive text-sm">
          {errorMessage}
        </p>
      ) : null}

      <button
        type="button"
        onClick={() => setLedgerOpen((open) => !open)}
        aria-expanded={ledgerOpen}
        aria-controls="punch-ledger"
        className={`flex min-h-[5.25rem] w-full items-center gap-3.5 rounded-xl border border-border bg-card px-5 py-4 text-left transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background`}
      >
        <LedgerMark />
        <span className="font-semibold text-foreground text-lg">
          {t('timesheets:punch.recent')}
        </span>
        {/*
          A COUNT, not the week total. The total was here and the "My week" card sits directly
          below with the same figure, so the screen answered "how many hours this week" twice. The
          count says what opening this will show instead. The total inside the ledger now reads the
          same week-grid response "My week" does, so the two can no longer disagree.
        */}
        <span className="ml-auto font-mono text-muted-foreground text-sm tabular-nums">
          {week.entriesPending || week.entriesFailed
            ? '—'
            : t('timesheets:punch.punchCount', { count: entries.length })}
        </span>
      </button>

      {/*
        `hidden` rather than unmounted, so the table is in the document for a find-in-page and the
        toggle has something real to control. Content is never gated on an animation.
      */}
      <section
        id="punch-ledger"
        hidden={!ledgerOpen}
        aria-labelledby="punch-ledger-heading"
        className="rounded-xl border border-border bg-card p-5"
      >
        <h2 id="punch-ledger-heading" className="mb-3 font-semibold text-foreground text-lg">
          {t('timesheets:punch.thisWeek')}
        </h2>
        {week.entriesPending ? (
          <p className="text-muted-foreground text-sm">{t('common:status.loading')}</p>
        ) : week.entriesFailed ? (
          <div role="alert" className="flex items-center justify-between gap-3 text-sm">
            <span className="text-destructive">{t('timesheets:punch.weekUnavailable')}</span>
            <button
              type="button"
              onClick={week.refetch}
              aria-label={t('timesheets:punch.retryWeek')}
              className="font-medium text-foreground underline underline-offset-4"
            >
              {t('common:error.retry')}
            </button>
          </div>
        ) : entries.length === 0 ? (
          // A 400 here means the account has no linked employee record, which is a real state for an
          // office login and not a broken screen. Both answers read the same way: nothing to show.
          <p className="text-muted-foreground text-sm">{t('timesheets:punch.noPunches')}</p>
        ) : (
          <table className="w-full border-collapse text-sm">
            <caption className="sr-only">{t('timesheets:punch.tableCaption')}</caption>
            <thead>
              <tr className="border-border border-b text-muted-foreground">
                <th scope="col" className="py-2 pr-2 text-left font-medium text-xs">
                  {t('timesheets:punch.job')}
                </th>
                <th scope="col" className="py-2 px-2 text-left font-medium text-xs">
                  {t('timesheets:punch.in')}
                </th>
                <th scope="col" className="py-2 px-2 text-left font-medium text-xs">
                  {t('timesheets:punch.out')}
                </th>
                <th scope="col" className="py-2 pl-2 text-right font-medium text-xs">
                  {t('timesheets:punch.hours')}
                </th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id} className="border-border border-t first:border-t-0">
                  <td className="break-words py-2 pr-2 text-foreground">
                    {entry.jobName ?? t('timesheets:punch.generalTime')}
                  </td>
                  <td className="whitespace-nowrap px-2 py-2 font-mono text-muted-foreground tabular-nums">
                    {punchTime(entry.clockInAt)}
                  </td>
                  <td className="whitespace-nowrap px-2 py-2 font-mono text-muted-foreground tabular-nums">
                    {entry.clockOutAt ? punchTime(entry.clockOutAt) : t('timesheets:punch.open')}
                  </td>
                  <td className="whitespace-nowrap py-2 pl-2 text-right font-mono text-foreground tabular-nums">
                    {entry.hours}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-border border-t-2">
                <th scope="row" colSpan={3} className="py-2 pr-2 text-left font-semibold">
                  {t('timesheets:punch.bookedSoFar')}
                </th>
                <td className="py-2 pl-2 text-right font-mono font-semibold text-foreground tabular-nums">
                  {weekHours ?? '—'}
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </section>

      <ConfirmDialog
        open={confirmingDuplicate && duplicates.length > 0}
        heading={t('timesheets:myClock.duplicateSiteHeading')}
        body={t('timesheets:myClock.duplicateSiteBody', { count: duplicates.length })}
        confirmLabel={t('timesheets:myClock.duplicateSiteCreateAnyway')}
        cancelLabel={t('timesheets:myClock.confirmNewSiteCancel')}
        confirmPending={clockIn.isPending}
        onConfirm={() => {
          if (target) submitPunch(target);
        }}
        onCancel={() => setConfirmingDuplicate(false)}
      >
        <ul className="mt-3 flex flex-col gap-2">
          {duplicates.map((job) => (
            <li key={job.id}>
              <button
                type="button"
                disabled={clockIn.isPending}
                onClick={() => submitPunch({ kind: 'job', job })}
                className="w-full rounded-md border border-border bg-card px-3 py-2 text-left text-foreground text-sm hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {t('timesheets:myClock.duplicateSiteUse', { job: job.jobName })}
              </button>
            </li>
          ))}
        </ul>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirmingTyped && target?.kind === 'typed'}
        heading={t('timesheets:myClock.confirmNewSiteHeading')}
        body={t('timesheets:myClock.confirmNewSiteBody', {
          address: target ? labelForTarget(target) : '',
        })}
        confirmLabel={t('timesheets:myClock.confirmNewSiteConfirm')}
        cancelLabel={t('timesheets:myClock.confirmNewSiteCancel')}
        confirmPending={clockIn.isPending}
        onConfirm={() => {
          if (target) submitPunch(target);
        }}
        onCancel={() => setConfirmingTyped(false)}
      />
    </div>
  );
}
